/**
 * JSON control-plane protocol. Two sockets per client:
 *  - lobby socket  (/ws)            presence, matchmaking, rooms, challenges, chat push
 *  - game socket   (/game?ticket=)  one match; binary state frames + a few JSON events
 * Splitting them lets game sockets live on dedicated game nodes later
 * while the lobby stays on stateless gateway nodes.
 */
import type { GameConfig, GameMode, GameResult } from '../engine/types.js';
import type { ArenaLives, MatchVariant } from '../engine/variants.js';
import type { TierId } from '../progression.js';

export type QueueMode = 'arena' | 'duel' | 'ffa4' | 'ffa6' | 'teams2' | 'koth4';
export const QUEUE_MODES: QueueMode[] = ['arena', 'duel', 'ffa4', 'ffa6', 'teams2', 'koth4'];
/** Humans needed to start. The big arena starts with whoever is waiting and fills up with bots. */
export const QUEUE_SIZES: Record<QueueMode, number> = { arena: 1, duel: 2, ffa4: 4, ffa6: 6, teams2: 4, koth4: 4 };
/** Duels stay pure skill; the bigger free-for-alls are more fun with power-ups. */
export const QUEUE_VARIANTS: Record<QueueMode, MatchVariant> = {
  arena: 'endless',
  duel: 'classic',
  ffa4: 'powerups',
  ffa6: 'powerups',
  teams2: 'powerups',
  koth4: 'koth',
};
/** Queues played as two teams (2 vs 2). */
export const TEAM_QUEUES: readonly QueueMode[] = ['teams2'];
/** Big arena time limit the player picks: 0 = none (play as long as you like), or rounds of 10 / 30 minutes. */
export type ArenaMinutes = 0 | 10 | 30;
export const ARENA_MINUTES: ArenaMinutes[] = [0, 10, 30];

export interface PublicUser {
  id: string;
  username: string;
  displayName: string;
  avatar: string | null;
  /** Profile photo from Google sign-in (shown when no preset avatar is picked). */
  avatarUrl: string | null;
  country: string | null;
  isGuest: boolean;
  isPremium: boolean;
  rating: number;
}

export interface RoomPlayer {
  userId: string;
  displayName: string;
  team: number;
  ready: boolean;
  isHost: boolean;
  skin: string;
  /** Death effect (absent: classic). */
  effect?: string;
}
export interface RoomState {
  code: string;
  hostId: string;
  mode: GameMode;
  /** Missing on rooms created before variants existed: treat as classic. */
  variant?: MatchVariant;
  maxPlayers: number;
  players: RoomPlayer[];
  status: 'open' | 'starting' | 'in_game';
}

export interface ChallengeInfo {
  id: string;
  from: Pick<PublicUser, 'id' | 'displayName' | 'avatar' | 'avatarUrl'>;
  toUserId: string;
  mode: QueueMode;
  expiresAt: number;
}

/** Friends playing together: the leader queues (or drops into an arena) for everyone. */
export interface PartyState {
  id: string;
  leaderId: string;
  members: Array<Pick<PublicUser, 'displayName' | 'avatar' | 'avatarUrl' | 'isPremium'> & { userId: string }>;
}
export const PARTY_MAX = 4;

export interface PartyInvite {
  partyId: string;
  from: Pick<PublicUser, 'id' | 'displayName' | 'avatar' | 'avatarUrl'>;
  expiresAt: number;
}

export interface ClanChatMessage {
  id: string;
  clanId: string;
  fromUserId: string;
  fromName: string;
  body: string;
  createdAt: string;
}

/** What one match earned a player (shown on the results screen). */
export interface MatchRewards {
  coins: number;
  xp: number;
  /** Battle-pass level after this match, and whether it just went up. */
  passLevel: number;
  levelUp: boolean;
  /** Ranked only: season rank points before/after and the tier now. */
  rp: { before: number; after: number; tier: TierId; division: 1 | 2 | 3 | null } | null;
}

export interface ChatMessage {
  id: string;
  fromUserId: string;
  toUserId: string;
  body: string;
  createdAt: string;
  clientId?: string;
}

export interface MatchPlayerInfo {
  slot: number;
  userId: string;
  name: string;
  team: number;
  skin: string;
  /** Death effect (absent: classic, as for bots). */
  effect?: string;
  isBot: boolean;
  country: string | null;
  rating: number;
  /** Persistent arenas: an empty seat (not on the board). */
  parked?: boolean;
  /** Lives this player chose (absent = unlimited). */
  lives?: number;
  /** The arena boss (a giant bot). */
  boss?: boolean;
}

export type ClientLobbyMsg =
  | { t: 'queue.join'; mode: QueueMode; arenaMinutes?: ArenaMinutes; arenaLives?: ArenaLives }
  | { t: 'queue.leave' }
  | { t: 'room.create'; mode: GameMode; maxPlayers: number; variant?: MatchVariant }
  | { t: 'room.join'; code: string }
  | { t: 'room.leave' }
  | { t: 'room.team'; team: number }
  | { t: 'room.ready'; ready: boolean }
  | { t: 'room.kick'; userId: string }
  | { t: 'room.start'; fillWithBots?: boolean }
  | { t: 'challenge.send'; toUserId: string; mode: QueueMode }
  | { t: 'challenge.respond'; challengeId: string; accept: boolean }
  | { t: 'challenge.cancel'; challengeId: string }
  | { t: 'party.invite'; toUserId: string }
  | { t: 'party.respond'; partyId: string; accept: boolean }
  | { t: 'party.leave' }
  | { t: 'party.kick'; userId: string }
  /** After a match: ask to play the same people again (`want: false` takes it back). */
  | { t: 'rematch'; matchId: string; want: boolean }
  | { t: 'ping'; ts: number };

export type ServerLobbyMsg =
  | { t: 'hello'; userId: string; serverTime: number; region: string }
  | { t: 'pong'; ts: number; serverTime: number }
  | { t: 'queue.status'; state: 'searching' | 'idle'; mode?: QueueMode; since?: number; playersSearching?: number; /** Recent average wait in this queue. */ typicalWaitMs?: number }
  | { t: 'match.found'; matchId: string; ticket: string; gameUrl: string }
  | { t: 'room.state'; room: RoomState }
  | { t: 'room.closed'; reason: 'left' | 'kicked' | 'host_left' | 'expired' }
  | { t: 'party.state'; party: PartyState }
  | { t: 'party.invite'; invite: PartyInvite }
  | { t: 'party.closed'; reason: 'left' | 'kicked' | 'disbanded' }
  /** Who has asked for a rematch of this match so far; it starts once all `total` players have. */
  | { t: 'rematch.status'; matchId: string; ready: string[]; total: number }
  | { t: 'challenge.incoming'; challenge: ChallengeInfo }
  | { t: 'challenge.sent'; challenge: ChallengeInfo }
  | { t: 'challenge.update'; challengeId: string; status: 'accepted' | 'declined' | 'cancelled' | 'expired'; blockedUntil?: number }
  | { t: 'friend.request'; from: PublicUser }
  | { t: 'friend.accepted'; user: PublicUser }
  | { t: 'presence'; userId: string; status: 'online' | 'offline' | 'in_game' }
  | { t: 'chat.message'; message: ChatMessage }
  | { t: 'clan.message'; message: ClanChatMessage }
  | { t: 'clan.kicked'; clanId: string }
  /** Something about a tournament you're in changed (bracket, your next match): refetch it. */
  | { t: 'tournament.update'; tournamentId: string }
  | { t: 'error'; code: ErrorCode; message: string };

export type ClientGameMsg =
  | { t: 'ready' }
  | { t: 'forfeit' }
  | { t: 'emote'; id: string }
  | { t: 'resync' }
  /** Persistent arenas: out of lives, start a fresh run in the same seat. */
  | { t: 'restart' }
  /** Hold / release boost (modes with `rules.boost`). */
  | { t: 'boost'; on: boolean };

export interface RatingChange {
  userId: string;
  before: number;
  after: number;
}

export type ServerGameMsg =
  | {
      t: 'match.init';
      matchId: string;
      yourSlot: number;
      ranked: boolean;
      config: GameConfig;
      players: MatchPlayerInfo[];
      voice: { enabled: boolean; teamChannels: boolean };
      /** Watching, not playing: yourSlot is -1 and inputs are ignored. */
      spectator?: boolean;
      /** Spectators: the slot to follow with the camera (the friend being watched). */
      follow?: number;
      /** The server takes turns for a given tick (BIN_INPUT_AT). Absent from servers older than that. */
      scheduledTurns?: boolean;
    }
  | { t: 'spectators'; count: number }
  | { t: 'player.left'; slot: number }
  /** Persistent arenas: someone took over a seat, left, or a bot came or went. */
  | { t: 'roster'; players: MatchPlayerInfo[] }
  | { t: 'emote'; slot: number; id: string }
  | {
      t: 'match.end';
      result: GameResult;
      ratingChanges: RatingChange[];
      achievements: string[];
      replayAvailable: boolean;
      /** Persistent arenas: the next round starts in the same arena after this long. */
      nextRoundInMs?: number;
      rewards?: MatchRewards;
      /** The same players can ask for a rematch (lobby `rematch` message). */
      rematch?: boolean;
    }
  | { t: 'error'; code: ErrorCode; message: string };

export type ErrorCode =
  | 'bad_request'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'rate_limited'
  | 'room_full'
  | 'room_not_found'
  | 'already_in_queue'
  | 'not_friends'
  | 'user_offline'
  | 'user_busy'
  | 'challenge_blocked'
  | 'premium_required'
  | 'insufficient_coins'
  | 'conflict'
  | 'internal';

export const EMOTES = ['gg', 'wow', 'lol', 'angry', 'cool', 'sad'] as const;
export type EmoteId = (typeof EMOTES)[number];
